"use client"

import { useEffect, useRef } from "react"
import { useRouter } from "next/navigation"
import { useSession } from "next-auth/react"
import { useTelegramMiniApp } from "@/hooks/use-telegram-miniapp"
import { toast } from "@/hooks/use-toast"
import { locales, type Locale } from "@/types/i18n"

const LOCALE_COOKIE = "NEXT_LOCALE"
// One-shot guard so the post-auth reload can't loop if the cookie isn't ready.
const RELOAD_FLAG = "tma_auth_reloaded"

/**
 * Background component that auto-authenticates users running inside Telegram Mini App.
 * Placed in the root layout so it runs on every page.
 *
 * Flow:
 * 1. Detect Telegram Mini App environment.
 * 2. If no locale cookie, set it from Telegram user's language_code, then
 *    soft-refresh (router.refresh) so server components re-render in that
 *    locale. We must NOT window.location.reload() here — a hard reload races
 *    the auth request below and, worse, cancels next-auth's in-flight fetches,
 *    which sends the app to /api/auth/error (the "Error" screen).
 * 3. If not authenticated, POST the Telegram initData to
 *    /api/auth/telegram/miniapp. That server route validates initData,
 *    authenticates with Medusa, and sets the NextAuth session cookie,
 *    _medusa_jwt, and owlcat-identity in ONE response — no CSRF double-submit,
 *    so it doesn't race the cold iOS WKWebView cookie store the way the old
 *    client-side signIn("telegram-miniapp") did.
 * 4. router.refresh() so SessionProvider + middleware pick up the new cookies.
 */
export default function TelegramMiniAppAuth() {
  const { status } = useSession()
  const { isMiniApp, isReady, initData, webApp } = useTelegramMiniApp()
  const router = useRouter()
  const hasAttempted = useRef(false)

  // Clear the one-shot reload guard once authenticated so a later logout →
  // re-auth can reload again.
  useEffect(() => {
    if (status === "authenticated" && typeof window !== "undefined") {
      sessionStorage.removeItem(RELOAD_FLAG)
    }
  }, [status])

  // Sync Telegram user language to locale cookie (once, if no cookie set).
  useEffect(() => {
    if (!isReady || !isMiniApp || !webApp) return
    const tgLang = webApp.initDataUnsafe?.user?.language_code?.split("-")[0]
    if (!tgLang) return

    const hasLocaleCookie = document.cookie
      .split(";")
      .some((c) => c.trim().startsWith(`${LOCALE_COOKIE}=`))
    if (hasLocaleCookie) return

    const mapped = (locales as readonly string[]).includes(tgLang)
      ? (tgLang as Locale)
      : null
    if (mapped) {
      document.cookie = `${LOCALE_COOKIE}=${mapped};path=/;max-age=${365 * 24 * 60 * 60};samesite=lax`
      // Soft refresh — NOT window.location.reload(). A hard reload cancels
      // next-auth's in-flight fetches and drops the app on /api/auth/error.
      router.refresh()
    }
  }, [isReady, isMiniApp, webApp, router])

  useEffect(() => {
    if (hasAttempted.current) return
    if (!isReady || !isMiniApp || !initData) return
    if (status === "loading") return
    if (status === "authenticated") return

    hasAttempted.current = true

    async function authenticate() {
      try {
        const localeCookie = document.cookie
          .split(";")
          .find((c) => c.trim().startsWith(`${LOCALE_COOKIE}=`))
        const locale = localeCookie?.split("=")[1]?.trim() || undefined

        const res = await fetch("/api/auth/telegram/miniapp", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ initData, locale }),
        })

        if (res.ok) {
          // Session + _medusa_jwt + owlcat-identity cookies are set in this
          // response. iOS WKWebView persists Set-Cookie asynchronously, so a
          // soft router.refresh() often renders before the session cookie is
          // readable and the nav stays logged-out until the next open. Do a
          // full page reload instead (mirrors the portal Mini App flow) — the
          // reload's document request carries the now-persisted cookie and the
          // nav renders authenticated. One-shot guard avoids a reload loop if
          // the cookie somehow isn't ready.
          if (
            typeof window !== "undefined" &&
            !sessionStorage.getItem(RELOAD_FLAG)
          ) {
            sessionStorage.setItem(RELOAD_FLAG, "1")
            window.location.reload()
          } else {
            router.refresh()
          }
          return
        }

        let errorMessage = `HTTP ${res.status}`
        try {
          const data = await res.json()
          if (data?.error) errorMessage = data.error
        } catch {
          // non-JSON body
        }
        console.error("[TelegramMiniAppAuth] Sign-in failed:", errorMessage)
        toast({
          title: "Не удалось войти через Telegram",
          description: errorMessage,
          variant: "destructive",
        })
      } catch (error) {
        console.error("[TelegramMiniAppAuth] Error:", error)
        toast({
          title: "Не удалось войти через Telegram",
          description: error instanceof Error ? error.message : "Сетевая ошибка",
          variant: "destructive",
        })
      }
    }

    authenticate()
  }, [isReady, isMiniApp, initData, status, router])

  return null
}
